/// FOURJS_START_COPYRIGHT(D,2024)
/// Property of Four Js*
/// (c) Copyright Four Js 2024, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("ItemWidget", ["WidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * Generic item widget composed (inline) of a left icon, text element (label) and right icon
   * This class is intended to be used ONLY with unlayouted parts of the UI
   * (The sidebar Rail, the chrome bar, the start menu, ...)
   * @class ItemWidget
   * @memberOf classes
   * @extends classes.WidgetBase
   */
  cls.ItemWidget = context.oo.Class(cls.WidgetBase, function ($super) {
    return /** @lends classes.ItemWidget.prototype */ {
      __name: "ItemWidget",
      /**
       * Image widget used to manage icon
       * @type {classes.ImageWidget}
       */
      _imageWidget: null,

      /**
       * True if text shouldn't be displayed
       * @type {boolean}
       */
      _hideText: false,

      /**
       * True if icon shouldn't be displayed
       * @type {boolean}
       */
      _hideIcon: false,

      /**
       * Item widget can match multiple DOM elements
       * @type {Array<HTMLElement>}
       */
      _elements: null,
      /** @type {string|null} */
      _defaultTTFColor: null,
      /** @type {boolean} */
      _isListItem: false,

      /**
       * @inheritDoc
       */
      _initElement: function () {
        this._ignoreLayout = true;
        $super._initElement.call(this);

        this._elements = [];
        this._elements.push(this.getElement());
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._imageWidget) {
          this._imageWidget.destroy();
          this._imageWidget = null;
        }

        for (let element of this._elements) {
          if (this._isListItem) {
            element?.parentNode?.remove();
          } else {
            element?.remove();
          }
        }

        this._elements.length = 0;
        this._elements = null;

        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        // no layout
      },

      /**
       * Deep clone of the app item default element
       * @returns {HTMLElement}
       */
      cloneElement: function () {
        const clonedElement = this.getElement().cloneNode(true);
        this._elements.push(clonedElement);
        return clonedElement;
      },

      /**
       * Returns the image widget, null there isn't one
       * @returns {classes.ImageWidget}
       */
      getImageWidget: function () {
        return this._imageWidget;
      },

      /**
       * Returns the primary icon element or queries it from DOM
       * @param {number} index - The index of the item element to retrieve the icon from (default: 0)
       * @returns {HTMLElement}
       */
      getPrimaryIconElement: function (index = 0) {
        if (this._elements[index]) {
          return this._elements[index].getElementsByClassName("gbc_ItemWidget__primaryIcon")[0] || null;
        }
        return null;
      },

      /**
       * Returns the secondary icon element or queries it from DOM
       * @param {number} index - The index of the item element to retrieve the icon from (default: 0)
       * @returns {HTMLElement}
       */
      getSecondaryIconElement: function (index = 0) {
        if (this._elements[index]) {
          return this._elements[index].getElementsByClassName("gbc_ItemWidget__secondaryIcon")[0] || null;
        }
        return null;
      },

      /**
       * Set the item elements as current
       * this will have a visual effect on the item, and announces the item as the current
       * one in its navigable set to screen readers via aria-current="page".
       * @param {boolean} value Set to true to make this item current. False otherwise
       */
      setCurrent: function (value) {
        for (let element of this._elements) {
          cls.DOMElementHelper.toggleClasses(element, value, gbc.constants.widgetCssClasses.current);
        }
        this.setAriaAttribute("current", value ? "page" : null);
      },

      /**
       * ItemWidget can be mounted in multiple locations at once (e.g. an app item is cloned into
       * the sidebar rail and into the chromebar) — `_elements` holds every DOM clone. Override
       * setAriaAttribute so ARIA state is applied uniformly across every mount, the same way
       * addClasses / removeClass / setTitle already iterate over `_elements`.
       * After destroy() `_elements` is nulled, so async handlers that fire post-destroy must no-op.
       * @inheritDoc
       */
      setAriaAttribute: function (attrName, attrVal) {
        if (!attrName || !this._elements) {
          return;
        }
        for (let element of this._elements) {
          cls.DOMElementHelper.updateAttribute(element, "aria-" + attrName, attrVal);
        }
      },

      /**
       * Same rationale as {@link setAriaAttribute}: keep every clone in sync.
       * @inheritDoc
       */
      setAriaRole: function (roleName) {
        if (!this._elements) {
          return;
        }
        for (let element of this._elements) {
          cls.DOMElementHelper.updateAttribute(element, "role", roleName);
        }
      },

      /** @inheritDoc */
      addClasses: function (...classes) {
        for (let element of this._elements) {
          element.classList.add(...classes);
        }
      },

      /** @inheritDoc */
      addClass: function (css) {
        for (let element of this._elements) {
          element.classList.add(css);
        }
      },

      /** @inheritDoc */
      removeClass: function (css) {
        for (let element of this._elements) {
          element.classList.remove(css);
        }
      },

      /** @inheritDoc */
      toggleClass: function (className, className2, switcher) {
        for (let element of this._elements) {
          cls.DOMElementHelper.toggleClass(element, className, className2, switcher);
        }
      },

      setImage: function (image) {
        this.setPrimaryIcon(image);
      },

      /**
       * Set Default TTF color
       * @param {string} color - rgb formatted or css name
       */
      setDefaultTTFColor: function (color) {
        this._defaultTTFColor = color;
        this.setStyle(".zmdi", {
          color: color
        });
      },

      /**
       * Set Primary icon on each item elements
       * (By default the secondary icon is the one placed at the start)
       * @param {string} src
       */
      setPrimaryIcon: function (src) {
        if (!src) {
          this._removePrimaryIcon();
          return;
        }

        if (!this._imageWidget) {
          this._imageWidget = cls.WidgetFactory.createWidget("Image", this.getBuildParameters());
          this._imageWidget.setParentWidget(this);
          this._imageWidget.setAutoScale(true);
          this._imageWidget.setAcceptEventWhenWindowInactive(this.acceptEventWhenWindowInactive());
        }
        if (this._defaultTTFColor) {
          this._imageWidget.setDefaultColor(this._defaultTTFColor);
        }
        this._imageWidget.setSrc(src);
        this._imageWidget.setTitle(this.getTitle());

        this.addClasses("hasImage");
        for (let element of this._elements) {
          element.getElementsByClassName(this.getBEMPartName("primaryIcon"))[0].replaceChildren(this._imageWidget.getElement().cloneNode(true));
        }
      },

      /**
       * Remove existing primary icons
       * @private
       */
      _removePrimaryIcon: function () {
        if (!this._imageWidget) {
          return;
        }
        // remove existing image
        this._imageWidget.destroy();
        this._imageWidget = null;
        for (let element of this._elements) {
          element.getElementsByClassName(this.getBEMPartName("primaryIcon"))[0].replaceChildren();
          element.classList.remove("hasImage");
        }
      },

      setPrimaryIconText: function (text) {
        for (const element of this._elements) {
          const leftIcon = element.getElementsByClassName(this.getBEMPartName("primaryIcon"))[0];
          leftIcon.textContent = text;
        }
      },

      /**
       * Set secondary icon on each item elements
       * This icon must be an extension of .zmdi
       * (By default the secondary icon is the one placed at the end)
       * @param {string} className
       */
      setSecondaryIcon: function (className) {
        for (const element of this._elements) {
          const rightIcon = element.getElementsByClassName(this.getBEMPartName("secondaryIcon"))[0];
          if (this._rightIconClass) {
            rightIcon.classList.remove(this._rightIconClass);
          }
          if (className) {
            rightIcon.classList.add(className);
          }
          // Decorative glyph: keep it out of the item button's accessible name
          rightIcon.setAttribute("aria-hidden", "true");
        }
        this._rightIconClass = className;
      },

      setSecondaryIconText: function (text) {
        if (!this._elements) {
          return;
        }
        for (const element of this._elements) {
          const leftIcon = element.getElementsByClassName(this.getBEMPartName("secondaryIcon"))[0];
          leftIcon.textContent = text;
          // Textual badge (e.g. a count) is meaningful: expose it to assistive tech
          leftIcon.removeAttribute("aria-hidden");
        }
      },

      /**
       * Set the text/label on each item elements
       * @param {string} text
       */
      setText: function (text) {
        for (let element of this._elements) {
          const textElement = element.getElementsByClassName(this.getBEMPartName("text"))[0];
          textElement.textContent = text;
        }
        this._labelText = text || null;
        this._refreshAccessibleName();

        // client QA code
        if (["qa_dialog_ready", "qa_menu_ready"].indexOf(text) >= 0) {
          gbc.QAService.bindQAReadyButton(this);
        }
      },

      /**
       * Set the title (tooltip) on every element managed by this widget. The
       * comment is a description: it names the item only when there is no
       * label text (icon-only), so it never shadows the visible label.
       * @param {string?} title The text to set as title. If null or empty it'll remove the title attribute
       */
      setTitle: function (title) {
        const normalized = title || null;
        if (this._imageWidget) {
          this._imageWidget.setTitle(title);
        }
        for (let element of this._elements) {
          cls.DOMElementHelper.updateAttribute(element, "title", normalized);
        }
        this._comment = normalized;
        this._refreshAccessibleName();
      },

      /**
       * Bind a handler executed when click is executed on secondaryIcon
       * @param hook
       * @returns {Function} return handler reference
       */
      onClickSecondaryIcon: function (hook) {
        return this.when(context.constants.widgetEvents.clickSecondaryIcon, hook);
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        const target = domEvent.target;
        const elementIndex = this._elements.findIndex((el) => el.contains(target));
        const secondaryIcon = this.getSecondaryIconElement(elementIndex);

        if (secondaryIcon?.contains(target)) {
          this.emit(context.constants.widgetEvents.clickSecondaryIcon, domEvent);
        }
        this.emit(context.constants.widgetEvents.click, domEvent);
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * Keyboard activation of the focused item (accessibility mode routes
       * keys to the DOM-focused widget before dialog accelerators).
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this.isEnabled() && this._isActivationKey(keyString, repeat)) {
          this.emit(context.constants.widgetEvents.click, domKeyEvent);
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Indicates that item widget will be wrapped into a list element
       */
      setAsListItem: function () {
        this._isListItem = true;
      }
    };
  });
  cls.WidgetFactory.registerBuilder("Item", cls.ItemWidget);
});
